import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { SapSimulationScenario } from '@/types/domain/sap-simulator';
import { Cpu, AlertTriangle, CheckCircle2, Clock, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SapFailureInjectionPanelProps {
  activeScenario: SapSimulationScenario;
  onScenarioChange: (scenario: SapSimulationScenario) => void;
}

export const SapFailureInjectionPanel: React.FC<SapFailureInjectionPanelProps> = ({
  activeScenario,
  onScenarioChange,
}) => {
  const scenarios: { key: SapSimulationScenario; label: string; desc: string; icon: any; color: string }[] = [
    {
      key: 'SUCCESS',
      label: 'Standard Success (200 OK)',
      desc: 'Normal BAPI execution. Returns valid SAP PR number (45000XXX).',
      icon: CheckCircle2,
      color: 'text-emerald-600 border-emerald-500/20 bg-emerald-500/5',
    },
    {
      key: 'INVALID_COST_CENTER',
      label: 'Invalid Cost Center (Error 409)',
      desc: 'Simulates unmapped or invalid cost center validation rejection.',
      icon: AlertTriangle,
      color: 'text-rose-600 border-rose-500/20 bg-rose-500/5',
    },
    {
      key: 'TIMEOUT',
      label: 'SAP Gateway Timeout (30s)',
      desc: 'Simulates integration timeout without immediate response.',
      icon: Clock,
      color: 'text-amber-600 border-amber-500/20 bg-amber-500/5',
    },
    {
      key: 'SAP_UNAVAILABLE',
      label: 'SAP System Unavailable (503)',
      desc: 'Simulates SAP maintenance downtime. Triggers Retry Queue.',
      icon: ShieldAlert,
      color: 'text-purple-600 border-purple-500/20 bg-purple-500/5',
    },
  ];

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
            <Cpu className="h-4 w-4 text-primary" />
            Developer Simulation Control & Failure Injection Engine
          </CardTitle>
          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-primary/10 text-primary">
            Testing Mode Active
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-3">
        <p className="text-xs text-foreground-secondary font-medium">
          Select a response scenario below to test how ProcureAI handles success, validation exceptions, gateway timeouts, and retry queues.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {scenarios.map((sc) => {
            const Icon = sc.icon;
            const isSelected = activeScenario === sc.key;

            return (
              <div
                key={sc.key}
                onClick={() => onScenarioChange(sc.key)}
                className={cn(
                  'rounded-xl border p-3.5 cursor-pointer transition-all space-y-2 select-none',
                  sc.color,
                  isSelected ? 'ring-2 ring-primary shadow-md scale-[1.02]' : 'hover:border-primary/40'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-xs">
                    <Icon className="h-4 w-4" />
                    <span>{sc.label}</span>
                  </div>

                  {isSelected && <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />}
                </div>

                <p className="text-[11px] text-foreground-secondary leading-snug">{sc.desc}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
